/**
 * Integrytas Group - Messaging & Task Automation (MTA)
 * Page: MTA.RULES
 *
 * Configuration interface for automation rules. Authors mta_automation_rule
 * (entity + event + JSON conditions) and its mta_action rows (TASK or MESSAGE
 * on a channel, recipient spec, {{field}} templates). The engine fires these
 * from the Common Routine after-write seam.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { registerUdcHost } from '../../lib/udc_hosts';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'MTA.RULES';

registerUdcHost('MTA.RULES', 'mta_automation_rule', 'Automation Rules');

const RULE_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Code', required: true, col_span: 1 },
  { key: 'entity', type: 'text', label: 'Entity', col_span: 1 },
  { key: 'onEvent', type: 'select', label: 'On Event', col_span: 1 },
];
const RULE_CORE_LAYOUT = toUldCoreLayout(RULE_FIELD_DEFS);

const RULE_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',   defaultWidth: 220 },
  { key: 'entity_name',   header: 'Entity', defaultWidth: 220 },
  { key: 'on_event',      header: 'Event',  defaultWidth: 120 },
  { key: 'is_active',     header: 'Active', defaultWidth: 90  },
];

type OnEvent = 'ADD' | 'CHANGE' | 'DELETE' | 'ANY';
type ActionType = 'TASK' | 'MESSAGE';
type Channel = 'INAPP' | 'EMAIL' | 'WEBHOOK';

interface RuleRow {
  mta_automation_rule_id: number; internal_code: string; entity_name: string;
  on_event: OnEvent; conditions: unknown; is_active: 'Y' | 'N'; description: string | null;
}
interface ActionRow {
  mta_action_id: number; action_type: ActionType; channel: Channel | null;
  recipients: unknown; title_template: string | null; body_template: string | null;
  due_in_days: number | null; webhook_url: string | null; sort_order: number; is_active: 'Y' | 'N';
}

export interface MtaRuleManagerPageProps { session: AuthSession }

export function MtaRuleManagerPage({ session }: MtaRuleManagerPageProps) {
  return (
    <PersonalizationProvider pageCode="MTA.RULES" session={session}>
      <MtaRuleManagerPageInner session={session} />
    </PersonalizationProvider>
  );
}

function MtaRuleManagerPageInner({ session }: MtaRuleManagerPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<RuleRow | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the rules list.
  const [facets, setFacets] = useState<{ entity_name: string[]; on_event: string[]; is_active: string[] }>(
    { entity_name: [], on_event: [], is_active: ['Y'] },
  );

  const udcGrid = useUdcGrid('mta_automation_rule', 'MTA.RULES', { pkColumn: 'mta_automation_rule_id', rows: rows as unknown as Record<string, unknown>[] });
  const detail = useExpandedRows<number>();

  // §17.B13 related-children (expand/contract). A rule's actions are loaded in the
  // modal, not per list row, so the panel is inert here.
  // TODO: wire relations (surface mta_action rows once loaded per row).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('mta_automation_rule')
        .select('mta_automation_rule_id, internal_code, entity_name, on_event, conditions, is_active, description')
        .order('entity_name').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as RuleRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort().map(v => ({ value: v, label: v }));
    return {
      entity_name: uniq(rows.map(r => r.entity_name)),
      on_event:    uniq(rows.map(r => r.on_event)),
      is_active:   [{ value: 'Y', label: t('mta_rules.facet.active', 'Active') }, { value: 'N', label: t('mta_rules.facet.inactive', 'Inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.entity_name.length + facets.on_event.length + facets.is_active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ entity_name: [], on_event: [], is_active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesFacets = (r: RuleRow) =>
      (facets.entity_name.length === 0 || facets.entity_name.includes(r.entity_name)) &&
      (facets.on_event.length === 0    || facets.on_event.includes(r.on_event)) &&
      (facets.is_active.length === 0   || facets.is_active.includes(r.is_active));
    const base = !q ? rows : rows.filter(r => r.internal_code.toLowerCase().includes(q) || (r.entity_name ?? '').toLowerCase().includes(q) || r.on_event.toLowerCase().includes(q));
    return base.filter(matchesFacets).filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
  }, [rows, filter, facets, udcGrid]);

  const handleDelete = async (r: RuleRow): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'mta_automation_rule', 'mta_automation_rule_id', r.mta_automation_rule_id);
      if (e) throw e;
      await reload();
      return true;
    } catch (e) { setError((e as Error).message); return false; } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('mta_rules.heading', 'Automation Rules')}
          description={t('mta_rules.list.description', 'Rules that fire tasks or messages from the write path when an entity event matches.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('mta_rules.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('mta_rules.new_rule', 'New Rule')}
              </button>)}
            </>
          }
        />
        {error && (
          <ErrorBanner
            message={t('mta_rules.error_banner', "Couldn't complete the last automation-rules operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('mta_rules.filter_placeholder', 'Filter rules…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('mta_rules.col.entity_name', 'Entity')} options={facetOptions.entity_name} selected={facets.entity_name} onChange={v => setFacets(f => ({ ...f, entity_name: v }))} />
            <FacetFilter label={t('mta_rules.col.on_event', 'Event')}     options={facetOptions.on_event}    selected={facets.on_event}    onChange={v => setFacets(f => ({ ...f, on_event: v }))} />
            <FacetFilter label={t('mta_rules.col.is_active', 'Active')}    options={facetOptions.is_active}   selected={facets.is_active}   onChange={v => setFacets(f => ({ ...f, is_active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('mta_rules.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...RULE_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('mta_rules.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('mta_rules.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('mta_rules.col_actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.mta_automation_rule_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.mta_automation_rule_id)} hasChildren={hasRelated(r.mta_automation_rule_id)} onToggle={() => detail.toggle(r.mta_automation_rule_id)} expandLabel={t('mta_rules.related.expand', 'Show related records')} collapseLabel={t('mta_rules.related.collapse', 'Hide related')} tooltip={t('mta_rules.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono">{r.internal_code}</span>}
                        {col.key === 'entity_name'   && <span className="font-mono">{r.entity_name}</span>}
                        {col.key === 'on_event'      && <span className="text-csm-xs">{r.on_event}</span>}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right whitespace-nowrap">
                      {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('mta_rules.edit', 'Edit')}</button>)}
                      {canDelete && (<button onClick={e => { e.stopPropagation(); setDeleteTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('mta_rules.delete', 'Delete')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.mta_automation_rule_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.mta_automation_rule_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('mta_rules.empty_title', 'No automation rules')}
                      action={
                        canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('mta_rules.empty_new_rule', '+ New Rule')}
                        </button> : undefined
                      }
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <RuleModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('mta_rules.delete_title', 'Delete Rule?')}
          message={t('mta_rules.delete_message', 'Delete "{{code}}"? This cannot be undone.', { code: deleteTarget.internal_code })}
          onConfirm={async () => { const ok = await handleDelete(deleteTarget); if (ok) setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function RuleModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: RuleRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;

  const defaultConditions = { match: 'all', conditions: [{ column: 'status', op: 'changed_to', value: 'CLOSED' }] };
  const initialValues = {
    code: row?.internal_code ?? '',
    entity: row?.entity_name ?? '',
    onEvent: (row?.on_event ?? 'CHANGE') as OnEvent,
    active: row ? row.is_active === 'Y' : false,
    description: row?.description ?? '',
    condText: (() => { try { return JSON.stringify(row?.conditions ?? defaultConditions, null, 2); } catch { return '{}'; } })(),
  };
  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const udcForm = useUdcFormFields('mta_automation_rule', 'MTA.RULES', { pkColumn: 'mta_automation_rule_id', pk: row?.mta_automation_rule_id });
  const ruleCoreLayout = useMemo(() => [...RULE_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const ruleFieldDefs = useMemo(() => [...RULE_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('MTA.RULES.MODAL', ruleCoreLayout);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const ruleId = row?.mta_automation_rule_id;

  useEffect(() => {
    reset(initialValues);
    setInternalCodeError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.mta_automation_rule_id, isCreate]);

  const loadActions = async () => {
    if (!ruleId) return;
    const { data } = await apiDb.from('mta_action')
      .select('mta_action_id, action_type, channel, recipients, title_template, body_template, due_in_days, webhook_url, sort_order, is_active')
      .eq('mta_automation_rule_id', ruleId).order('sort_order');
    setActions((data ?? []) as ActionRow[]);
  };
  // loadActions is re-created each render; reload only when the selected ruleId changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadActions(); }, [ruleId]);

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const saveRule = async () => {
    setBusy(true); setError(null);
    try {
      let cond: unknown;
      try { cond = JSON.parse(form.condText); } catch { throw new Error(t('mta_rules.err_conditions_json', 'Conditions must be valid JSON.')); }
      if (isCreate && !form.code.trim()) throw new Error(t('mta_rules.err_code_required', 'Internal code is required.'));
      if (!form.entity.trim()) throw new Error(t('mta_rules.err_entity_required', 'Entity (table) name is required.'));
      const payload = { entity_name: form.entity.trim(), on_event: form.onEvent, conditions: cond, is_active: form.active ? 'Y' : 'N', description: form.description || null };
      if (isCreate) {
        const { error: e } = await routedInsert(session, 'mta_automation_rule', { internal_code: form.code.trim().toUpperCase(), ...payload });
        if (e) throw e;
      } else {
        const { error: e } = await routedUpdate(session, 'mta_automation_rule', 'mta_automation_rule_id', ruleId, payload);
        if (e) throw e;
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addAction = async () => {
    if (!ruleId) return;
    const order = (actions.length ? actions[actions.length - 1].sort_order : 0) + 1;
    const { error: e } = await routedInsert(session, 'mta_action', {
      mta_automation_rule_id: ruleId, action_type: 'TASK', recipients: [{ type: 'actor' }], sort_order: order,
    });
    if (e) { setError(e.message); return; }
    await loadActions();
  };
  const saveAction = async (a: ActionRow, recipientsText: string) => {
    let rec: unknown;
    try { rec = JSON.parse(recipientsText); } catch { setError(t('mta_rules.err_recipients_json', 'Recipients must be valid JSON.')); return; }
    await routedUpdate(session, 'mta_action', 'mta_action_id', a.mta_action_id, {
      action_type: a.action_type, channel: a.action_type === 'MESSAGE' ? a.channel : null,
      recipients: rec, title_template: a.title_template, body_template: a.body_template,
      due_in_days: a.due_in_days, webhook_url: a.webhook_url, sort_order: a.sort_order, is_active: a.is_active,
    });
    await loadActions();
  };
  const deleteAction = async (id: number) => {
    await routedDelete(session, 'mta_action', 'mta_action_id', id);
    setActions(as => as.filter(a => a.mta_action_id !== id));
  };

  const modalTitle = isCreate ? t('mta_rules.modal_title_create', 'Create Automation Rule') : t('mta_rules.modal_title_edit', 'Edit Automation Rule — {{code}}', { code: row?.internal_code ?? '' });

  return (
    <>
      <Modal
        title={modalTitle}
        onClose={handleClose}
        width="2xl"
        headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
        footer={
          <>
            <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('mta_rules.close', 'Close')}</button>
            <button onClick={() => void saveRule()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('mta_rules.saving', 'Saving…') : t('mta_rules.save_rule', 'Save Rule')}</button>
          </>
        }
      >
        <div className="mb-3">
          <FieldRenderer
            fields={ruleFieldDefs}
            layout={layout}
            columns={3}
            pageCode="MTA.RULES.MODAL"
            session={session}
            render={(f) => {
              if (f.key === 'code') return (
                <PersonalizedField componentKey="modal" fieldKey="code" label={t('mta_rules.field_code', 'Code')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('mta_rules.field_code', 'Code')}</label>
                  <input type="text" value={form.code} disabled={!isCreate}
                         onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                         onBlur={() => {
                           if (isCreate && form.code && !/^[A-Z][A-Z0-9_.]*$/.test(form.code)) {
                             setInternalCodeError(t('mta_rules.err_code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                           } else {
                             setInternalCodeError(null);
                           }
                         }}
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
                  {internalCodeError
                    ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                    : isCreate && <p className="text-csm-xs text-csm-ink-soft mt-1">{t('mta_rules.code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                  }
                </>
                </PersonalizedField>
              );
              if (f.key === 'entity') return (
                <PersonalizedField componentKey="modal" fieldKey="entity" label={t('mta_rules.field_entity', 'Entity')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('mta_rules.field_entity', 'Entity')}</label>
                  <input type="text" value={form.entity} onChange={e => setForm(f => ({ ...f, entity: e.target.value }))} placeholder="po_purchase_order"
                         className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" /></>
                </PersonalizedField>
              );
              if (f.key === 'onEvent') return (
                <PersonalizedField componentKey="modal" fieldKey="onEvent" label={t('mta_rules.field_on_event', 'On Event')}>
                <><label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('mta_rules.field_on_event', 'On Event')}</label>
                  <select value={form.onEvent} onChange={e => setForm(f => ({ ...f, onEvent: e.target.value as OnEvent }))} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
                    <option value="ADD">ADD</option><option value="CHANGE">CHANGE</option><option value="DELETE">DELETE</option><option value="ANY">ANY</option>
                  </select></>
                </PersonalizedField>
              );
              if (udcForm.isUdcKey(f.key)) return (
                <PersonalizedField componentKey="modal" fieldKey={f.key} label={f.label}
                  readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                  {udcForm.renderControl(f.key)}
                </PersonalizedField>
              );
              return null;
            }}
          />
        </div>

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('mta_rules.field_conditions', 'Conditions (JSON)')}</label>
        <textarea rows={6} value={form.condText} onChange={e => setForm(f => ({ ...f, condText: e.target.value }))}
                  className="mb-1 w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs focus:border-csm-watermark focus:outline-none" />
        <div className="mb-3 text-csm-2xs text-csm-ink-soft">{t('mta_rules.ops_hint', 'ops: = != > >= < <= changed changed_to changed_from (change ops need an audited entity).')}</div>

        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('mta_rules.field_description', 'Description')}</label>
        <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="mb-3 w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none" />

        <div className="mb-3 flex items-center gap-3">
          <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-gray-700" /> {t('mta_rules.active', 'Active')}
          </label>
        </div>

        {!isCreate && (
          <div className="border-t border-csm-border pt-2">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('mta_rules.actions_heading', 'Actions')}</span>
              <button onClick={() => void addAction()} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('mta_rules.add_action', '+ Action')}</button>
            </div>
            {actions.map((a, idx) => <ActionEditor key={a.mta_action_id} action={a}
              onChange={patch => setActions(as => as.map((x, i) => i === idx ? { ...x, ...patch } : x))}
              onSave={recText => void saveAction(actions[idx], recText)}
              onDelete={() => void deleteAction(a.mta_action_id)} />)}
            {actions.length === 0 && <div className="text-csm-xs italic text-csm-watermark">{t('mta_rules.no_actions', 'No actions yet.')}</div>}
          </div>
        )}

        {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
        {isCreate && <div className="mt-2 text-csm-2xs text-csm-watermark">{t('mta_rules.save_first', 'Save the rule first to add actions.')}</div>}
      </Modal>

      {showDiscard && (
        <ConfirmModal
          title={t('mta_rules.discard_title', 'Discard changes?')}
          message={t('mta_rules.discard_message', 'You have unsaved changes that will be lost.')}
          confirmLabel={t('mta_rules.discard_confirm', 'Discard')}
          cancelLabel={t('mta_rules.discard_cancel', 'Keep editing')}
          danger={false}
          onConfirm={() => { reset(); onClose(); }}
          onCancel={() => setShowDiscard(false)}
        />
      )}
    </>
  );
}

function ActionEditor({ action, onChange, onSave, onDelete }: {
  action: ActionRow; onChange: (patch: Partial<ActionRow>) => void; onSave: (recipientsText: string) => void; onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [recText, setRecText] = useState(() => { try { return JSON.stringify(action.recipients ?? []); } catch { return '[]'; } });
  return (
    <div className="mb-2 rounded border border-csm-border-soft p-2">
      <div className="mb-1 flex flex-wrap items-center gap-1">
        <select value={action.action_type} onChange={e => onChange({ action_type: e.target.value as ActionType })} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
          <option value="TASK">TASK</option><option value="MESSAGE">MESSAGE</option>
        </select>
        {action.action_type === 'MESSAGE' && (
          <select value={action.channel ?? 'INAPP'} onChange={e => onChange({ channel: e.target.value as Channel })} className="h-7 rounded border border-csm-border px-1 text-csm-xs">
            <option value="INAPP">INAPP</option><option value="EMAIL">EMAIL</option><option value="WEBHOOK">WEBHOOK</option>
          </select>
        )}
        {action.action_type === 'TASK' && (
          <input type="number" value={action.due_in_days ?? ''} onChange={e => onChange({ due_in_days: e.target.value === '' ? null : Number(e.target.value) })} placeholder={t('mta_rules.action_due_placeholder', 'due+days')} className="h-7 w-20 rounded border border-csm-border px-1 text-csm-xs" />
        )}
        <button onClick={() => onSave(recText)} className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">{t('mta_rules.action_save', 'Save')}</button>
        <button onClick={onDelete} className="ml-auto text-csm-danger text-csm-xs">{t('mta_rules.action_delete', 'delete')}</button>
      </div>
      <input type="text" value={action.title_template ?? ''} onChange={e => onChange({ title_template: e.target.value })} placeholder={t('mta_rules.action_title_placeholder', 'Title template — use {{column}}')} className="mb-1 h-7 w-full rounded border border-csm-border px-1 text-csm-xs" />
      <input type="text" value={action.body_template ?? ''} onChange={e => onChange({ body_template: e.target.value })} placeholder={t('mta_rules.action_body_placeholder', 'Body template — use {{column}}')} className="mb-1 h-7 w-full rounded border border-csm-border px-1 text-csm-xs" />
      <input type="text" value={recText} onChange={e => setRecText(e.target.value)} placeholder={t('mta_rules.action_recipients_placeholder', 'Recipients JSON e.g. [{"type":"actor"},{"type":"role","role":"FINANCE"}]')} className="mb-1 h-7 w-full rounded border border-csm-border px-1 font-mono text-csm-2xs" />
      {action.action_type === 'MESSAGE' && action.channel === 'WEBHOOK' && (
        <input type="text" value={action.webhook_url ?? ''} onChange={e => onChange({ webhook_url: e.target.value })} placeholder="https://webhook.url" className="h-7 w-full rounded border border-csm-border px-1 text-csm-xs" />
      )}
    </div>
  );
}
